import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useQueryUserBillingInfo } from '../../../data/query/useQueryUserBillingInfo';
import { getCommerceBillingUiPorts } from '../../settings/billing/billing-ui-ports';
import { pricingPlansModalOpenAtom } from '../../settings/billing/billingAtoms';
import { isHeidi2OptInCreditUsageEnabled } from '../../settings/is-credit-usage-surface-enabled';
import {
  resolveEvidenceUpgradeModalTarget,
  type EvidenceUpgradeModalTarget,
} from './resolveEvidenceUpgradeModalTarget';

export type OpenEvidenceUpgradeModalOptions = {
  isScribePlusUser: boolean;
  isClinicianTrialUser: boolean;
  isPracticeUpgradeTarget: boolean;
  /** Opens the pricing plans modal instead for the Heidi 2 opt-in cohort. */
  preferPricingPlansModal?: boolean;
};

export function useOpenEvidenceUpgradeModal() {
  const setClinicianUpgradeModalOpen =
    getCommerceBillingUiPorts().useSetClinicianUpgradeModalOpen();
  const setPracticeUpgradeModalOpen =
    getCommerceBillingUiPorts().useSetPracticeUpgradeModalOpen();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const isOnHeidi2 = isHeidi2OptInCreditUsageEnabled({
    isHeidi2OptInEnabled: getCommerceBillingUiPorts().useIsHeidi2OptInEnabled(),
    heidiVersion: billingInfo?.heidi_version,
  });
  const isFullUpgradeFlowEnabled =
    getCommerceBillingUiPorts().useIsHeidi2FullUpgradeFlowEnabled();
  const setPricingPlansModalOpen = useSetAtom(pricingPlansModalOpenAtom);

  const openModalForTarget = useCallback(
    (target: EvidenceUpgradeModalTarget) => {
      switch (target) {
        case 'clinician':
          setClinicianUpgradeModalOpen(true);
          break;
        case 'practice':
          setPracticeUpgradeModalOpen(true);
          break;
      }
    },
    [setClinicianUpgradeModalOpen, setPracticeUpgradeModalOpen]
  );

  const openEvidenceUpgradeModal = useCallback(
    ({
      isScribePlusUser,
      isClinicianTrialUser,
      isPracticeUpgradeTarget,
      preferPricingPlansModal = false,
    }: OpenEvidenceUpgradeModalOptions) => {
      // The pricing plans modal sells Heidi 2 features, so only opt-in users already on
      // Heidi 2 see it, on the same gates as the sidebar tile, and only where it is
      // mounted (with the Evidence trial-and-upgrade flows).
      if (
        preferPricingPlansModal &&
        isOnHeidi2 &&
        isFullUpgradeFlowEnabled &&
        getCommerceBillingUiPorts().isEvidenceLaunchTrialAndUpgradeFlowsEnabled()
      ) {
        setPricingPlansModalOpen(true);
        return;
      }
      const target = resolveEvidenceUpgradeModalTarget({
        isScribePlusUser,
        isClinicianTrialUser,
        isPracticeUpgradeTarget,
      });
      openModalForTarget(target);
    },
    [isOnHeidi2, isFullUpgradeFlowEnabled, openModalForTarget, setPricingPlansModalOpen]
  );

  return { openEvidenceUpgradeModal };
}
